import { actions } from 'pages/AutomationEngine/store';
import {
  isBranchSplit,
  isConditionOrGoalSplit,
} from 'pages/AutomationEngine/store/ts-utilities';
import {
  isPersistPending,
  waitForStore,
  type AutomationStep,
  type AutomationStoreLike,
} from './codeStepSave';
import { isPersistedStepId } from './codeStepAiGate';

export type SaveFirstOutcome =
  | 'busy'
  | 'invalid'
  | 'failed'
  | 'go_to_loop'
  | 'timeout'
  | 'unresolved'
  | 'opened';

const findConfirmedStepId = (
  stepId: string | undefined,
  before: readonly AutomationStep[],
  after: readonly AutomationStep[]
): string | null => {
  if (stepId) {
    return after.some(({ id }) => id === stepId) ? stepId : null;
  }
  const beforeIds = new Set(before.map(({ id }) => id));
  const added = after.filter(
    ({ id, type }) => type === 'code_step' && !beforeIds.has(id)
  );
  return added.length === 1 ? added[0].id : null;
};

const sentToApi = (step: AutomationStep) =>
  !(isConditionOrGoalSplit(step as never) || isBranchSplit(step as never));

export const resolveServerStepId = (
  clientStepId: string,
  sentSteps: readonly AutomationStep[],
  savedSteps: readonly AutomationStep[]
): string | null => {
  const sent = sentSteps.filter(sentToApi);
  const saved = savedSteps.filter(sentToApi);
  const index = sent.findIndex(({ id }) => id === clientStepId);
  if (index === -1 || sent.length !== saved.length) return null;
  const { id } = saved[index];
  return isPersistedStepId(id) ? id : null;
};

export interface SaveFirstWithStoreOptions {
  store: AutomationStoreLike;
  stepId: string | undefined;
  confirm: () => boolean;
  beforePersist: () => void;
  open: (stepId: string) => void;
  confirmTimeoutMs: number;
  saveTimeoutMs: number;
}

export const saveFirstWithStore = async ({
  store,
  stepId,
  confirm,
  beforePersist,
  open,
  confirmTimeoutMs,
  saveTimeoutMs,
}: SaveFirstWithStoreOptions): Promise<SaveFirstOutcome> => {
  const before = store.getState().steps;
  if (!confirm()) return 'invalid';
  const clientStepId = await waitForStore(
    store,
    (state) =>
      state.editStepInfo || state.createStepInfo
        ? null
        : findConfirmedStepId(stepId, before, state.steps),
    confirmTimeoutMs
  );
  if (!clientStepId) return 'invalid';

  const sentSteps = store.getState().steps;
  beforePersist();
  const started = store.dispatch(actions.persistAutomation());
  if (!isPersistPending(started)) return 'go_to_loop';
  const { requestId } = started.meta;
  const last = await waitForStore(
    store,
    ({ lastPersist }) =>
      lastPersist?.requestId === requestId ? lastPersist : null,
    saveTimeoutMs
  );
  if (!last) return 'timeout';
  if (!last.ok) return 'failed';

  const serverStepId = resolveServerStepId(
    clientStepId,
    sentSteps,
    store.getState().steps
  );
  if (!serverStepId) return 'unresolved';
  open(serverStepId);
  return 'opened';
};
